import axios, { AxiosError } from 'axios';
import { auth } from '../firebase'; // your firebase.ts file
import { storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import {
  logLogoUploadBytesDiagnostics,
  logLogoUnicodeDiagnostics,
  resolveLogoUploadPayload,
} from "../components/CompanyIdentity/logoUtils";



interface ApiError {
  message?: string;
}

export const backendAPI = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

interface Business {
  id: number;
  name: string;
  registryId: string;
  status: string;
  location: string;
  jurisdictionCountry?: string;
  businessId: number;
  userId: number;
  createdAt: string;
  updatedAt: string;
  templateId?: number | null;
  logo?: string | null;
  details?: {
    description?: string;
    investment_level?: string;
    sector?: string;
    scalability?: string;
    economic_activity?: string;
    primary_color?: string;
    secondary_color?: string;
    font?: string;
  };
}

interface Answers {
  userDescription: string;
  hasIdea: boolean;
  ideaDescription: string;
  interestsAndMotivations: string[];
  skills: string[];
  resources: string[];
  willingToLearn: string;
  timeAvailability: string;
  riskLevel: string;
  typeOfClients: string[];
  diferentiationStrategy: string[];
  marketTrendsKnowledge: string;
  interestedInEmergingSectors: string;
  scalability: string;
  partnerships: string;
  legal: string;
  financial: string;
}


type Profile = {
  avatar?: string;
  info?: string;
  skills: string[];
  interests: string[];
};

type Motivation =
  | "Libertad"
  | "Ingreso extra"
  | "Impacto"
  | "Construir en grande"
  | "Creatividad";

type Idea = {
  id: string;
  title: string;
  why: string;
  tags: string[];
  type: "ecom" | "service" | "content" | "saas";
};

type Refinement = {
  offering: string;
  mode: "solo" | "team";
};


interface IdeaGeneratorPayload {
  profile: Profile;
  motivations: Motivation[];
  context?: string;
}


export type JourneyResult = {
  profile: Profile;
  motivations: Motivation[];
  selectedIdea: Idea | null;
  refinement: Refinement;
  companyImage: CompanyImage;
  recommendedTools: ToolCategory[];
  onboardingPath?: "no_idea" | "has_idea" | "established";
  acquisitionContext?: string;
  businessDraft?: Record<string, unknown>;
};

interface CompanyImage {
  logo_svg?: string;
  logo_url?: string;
  primaryColor?: string;
  secondaryColor?: string;
  tertiaryColor?: string;
  font?: string;
}

interface ToolCategory {
  id: number;
  name: string;
}


export const get_user_business = async (businessId: number) => {
  try {
    const response = await backendAPI.get(
      `businesses/user_business?businessId=${businessId}`,
    );

    return response.data;
  } catch (error: any) {
    if (error?.response) {
      console.error("[get_user_business] API Error:", {
        status: error.response.status,
        data: error.response.data,
      });
    }
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
        "Get user business failed"
    );
  }
};

export const create_business = async (journeyResult: JourneyResult) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();

    const payload = {
      profile: journeyResult.profile,
      motivations: journeyResult.motivations,
      selectedIdea: journeyResult.selectedIdea,
      refinement: journeyResult.refinement,
      companyImage: journeyResult.companyImage,
      recommendedTools: journeyResult.recommendedTools,
      onboardingPath: journeyResult.onboardingPath,
      acquisitionContext: journeyResult.acquisitionContext,
    };

    let imageUrl: string | undefined;

    if (payload.companyImage.logo_svg) {
      imageUrl = await uploadBusinessLogo(payload.companyImage.logo_svg);
    }
    const businessPayload = {
      ...payload,
      companyImage: {
        ...payload.companyImage,
        logo_url: imageUrl,
      },
    };
    const response = await backendAPI.post(
      'businesses/create',
      { businessPayload},
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    return response;
  } catch (error: any) {
    throw new Error((error as AxiosError<ApiError>)?.response?.data?.message || 'Business creation failed');
  }
};

function firebaseStoragePathFromLogo(logoValue: string): string | null {
  const trimmed = logoValue.trim();
  if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) {
    return trimmed;
  }

  try {
    const url = new URL(trimmed);
    if (!url.hostname.includes("firebasestorage.googleapis.com")) {
      return null;
    }
    const objectSegment = url.pathname.match(/\/o\/(.+)$/);
    if (!objectSegment?.[1]) {
      return null;
    }
    return decodeURIComponent(objectSegment[1]);
  } catch {
    return null;
  }
}

async function deleteStoredLogo(logoValue: string) {
  const storagePath = firebaseStoragePathFromLogo(logoValue);
  if (!storagePath) {
    return;
  }
  await deleteObject(ref(storage, storagePath));
}

async function uploadBusinessLogo(content: string): Promise<string> {
  logLogoUnicodeDiagnostics("uploadBusinessLogo:input", content);

  const resolved = resolveLogoUploadPayload(content);

  if (resolved.kind === "url") {
    return resolved.url;
  }

  logLogoUploadBytesDiagnostics("uploadBusinessLogo:pre-firebase", resolved.bytes);

  const fileRef = ref(
    storage,
    `logos/${Date.now()}-logo.${resolved.extension}`
  );
  await uploadBytes(fileRef, resolved.bytes, {
    contentType: resolved.contentType,
  });
  return await getDownloadURL(fileRef);
}

export const edit_business = async (business: Business, new_logo_svg?: string) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();

    // If a new SVG is provided, upload and replace
    if (new_logo_svg) {
      // If there's an old logo, remove it from Firebase
      if (business.logo) {
        try {
          await deleteStoredLogo(business.logo);
        } catch (err) {
          console.warn("Could not delete old logo:", err);
        }
      }

      // Upload new logo (SVG markup, data URL, or remote URL)
      const imageUrl = await uploadBusinessLogo(new_logo_svg);
      business = { ...business, logo: imageUrl };
    }

    // Send updated business to backend
    const response = await backendAPI.put(
      "businesses/edit",
      { business },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
        "Business update failed"
    );
  }
};

export const get_user_businesses = async () => {
  try {

    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not logged in");
    }

    const idToken = await user.getIdToken();

    const response = await backendAPI.get("businesses/user_businesses", {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });


    return response;
  } catch (error: any) {
    if (error?.response) {
      console.error("[get_user_businesses] API Error Response:", {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers,
      });
    } else if (error?.request) {
      console.error("[get_user_businesses] No response received:", error.request);
    } else {
      console.error("[get_user_businesses] Error creating request:", error.message);
    }

    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || "Get user businesses failed"
    );
  }
};

export const get_base_business_recommendations = async (answers: Answers) => {
  try {
    const response = await backendAPI.post('businesses/business_recommendation', answers);
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Get business recommendations failed'
    );
  }
};

export const get_business_recommendations = async (payload: IdeaGeneratorPayload) => {
  try {
    const response = await backendAPI.post('businesses/business_recommendation', payload);
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Get business recommendations failed'
    );
  }
};

export const get_business_checklist = async (userBusinessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.get(
      'businesses/user_business_checklist',
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        params: {
          userBusinessId: userBusinessId,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Get business checklist failed'
    );
  }
};

export const get_business_activities = async (description: string) => {
  try {
    const response = await backendAPI.post('businesses/business_activities', { description });
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Get business activities failed'
    );
  }
};

export const get_business_recommendations_idea  = async (userDescription: string, ideaDescription: string, businessActivity: string) => {
  try {
    const response = await backendAPI.post('businesses/business_recommendation_idea', {userDescription, ideaDescription, businessActivity});
    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Get business recommendations from idea failed'
    );
  }
};

export const edit_business_query = async (name: string, description: string, query: string) => {
  try {

    const payload = { name, description, query };
    console.log("[edit_business_query] Sending payload:", payload);

    const response = await backendAPI.post(
      "businesses/edit_business_query/",
      payload,
    );

    console.log("[edit_business_query] Response received:", response);
    return response;
  } catch (error: any) {
    if ((error as AxiosError)?.response) {
      console.error("[edit_business_query] Axios error response:", (error as AxiosError).response);
    } else {
      console.error("[edit_business_query] Error:", error);
    }

    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || "Edit business query failed"
    );
  }
};

export const get_business_tool_public_key = async (businessId: number, toolName: string) => {
  try {

    const response = await backendAPI.get('businesses/fetch_tool', {
      params: { businessId, toolName },
    });

    return response;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Get business tool public key failed'
    );
  }
};

export const get_business_completion = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.get('businesses/business_completion', {
      params: { businessId },
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
      'Get business completion failed'
    );
  }
};

export const get_business_todo = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.get('businesses/to_do', {
      params: { businessId },
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
      'Get business completion failed'
    );
  }
};
export type GenerateCompanyLogoOptions = {
  backgroundColor?: string;
  sector?: string;
  economicActivity?: string;
  targetCustomer?: string;
  model?: string;
  offerStatement?: string;
};

export const generate_company_logo = async (
  name: string,
  description: string,
  primaryColor: string,
  secondaryColor: string,
  tertiaryColor: string,
  font: string,
  options: GenerateCompanyLogoOptions = {}
) => {
  try {
    const payload = {
      name,
      description,
      primaryColor,
      secondaryColor,
      tertiaryColor,
      font,
      ...options,
    };
    console.log("[generateCompanyLogo] Sending payload:", payload);

    const response = await backendAPI.post("businesses/generate_company_logo/", payload);

    console.log("[generateCompanyLogo] Response received:", response.data);
    return response.data;
  } catch (error: any) {
    if ((error as AxiosError)?.response) {
      console.error("[generateCompanyLogo] Axios error response:", (error as AxiosError).response);
    } else {
      console.error("[generateCompanyLogo] Error:", error);
    }

    throw new Error(
      (error as AxiosError<{ message?: string }>)?.response?.data?.message || "Generate company logo failed"
    );

  }
};

export const get_all_notifications = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();

    console.log("[get_all_notifications] Fetching notifications for business:", businessId);

    const response = await backendAPI.get("businesses/notifications/all", {
      params: { businessId },
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    console.log("[get_all_notifications] Response received:", response);
    return response.data;
  } catch (error: any) {
    console.error("[get_all_notifications] Error:", error);
    throw new Error(
      (error as AxiosError<{ message?: string }>)?.response?.data?.message  || "Failed to fetch notifications"
    );
  }
};

export const get_notification = async (businessId: number, notificationId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();

    console.log(`[get_notification] Fetching notification ${notificationId} for business ${businessId}`);

    const response = await backendAPI.get("businesses/notifications/notification", {
      params: { businessId, notificationId },
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    console.log("[get_notification] Response received:", response);
    return response.data;
  } catch (error: any) {
    console.error("[get_notification] Error:", error);
    throw new Error(
      (error as AxiosError<{ message?: string }>)?.response?.data?.message  || "Failed to fetch notification"
    );
  }
};

export const create_notification = async (businessId: number, description: string) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();

    const payload = { businessId, description };
    console.log("[create_notification] Sending payload:", payload);

    const response = await backendAPI.post("businesses/notifications/create", payload, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    console.log("[create_notification] Response received:", response);
    return response.data;
  } catch (error: any) {
    console.error("[create_notification] Error:", error);
    throw new Error(
      (error as AxiosError<{ message?: string }>)?.response?.data?.message  || "Failed to create notification"
    );
  }
};
